<template>
  <div class="TileDataElaadForecast">
    <TileDataContent
      :fids="fids"
      :forecast-label="forecastLabel"
      :dataset="dataset"
      :data-by-id="dataById"
    >
      <template #content-header>
        <TileDataContentHeader v-if="currentView !== 'toPlan'">
          <template #first-column>
            <b> {{ $t('components.map.tiles.ElaadForecast.expected') }} </b>
          </template>
          <template #second-column>
            <b> {{ $t('components.map.tiles.TileData.chargingStations') }} </b>
          </template>
          <template #third-column>
            <b> {{ $t('components.map.tiles.ElaadForecast.power') }} </b>
          </template>
        </TileDataContentHeader>
        <TileDataContentHeader v-else>
          <template #second-column />
        </TileDataContentHeader>
      </template>

      <template #sub-title>
        <p>
          <span
            :class="currentView === 'simple' ? 'currentView' : 'otherView'"
            @click="setView('simple')"
          > {{ $t('components.map.tiles.ElaadForecast.simpleView') }} </span> |
          <span
            :class="currentView === 'detailed' ? 'currentView' : 'otherView'"
            @click="setView('detailed')"
          > {{ $t('components.map.tiles.ElaadForecast.detailedView') }} </span> |
          <span
            :class="currentView === 'toPlan' ? 'currentView' : 'otherView'"
            @click="setView('toPlan')"
          > {{ $t('components.map.tiles.ElaadForecast.toPlan') }} </span>
        </p>
      </template>

      <template #sub-title2>
        <p>
          <b> {{ $t('components.map.tiles.ElaadForecast.totalEVs') }} </b>
          {{ formatNumber(getRaw(generateFieldName("aantal_evs", year)), 0) }}
        </p>
      </template>

      <template #content>
        <!-- SIMPLE VIEW -->
        <template v-if="currentView === 'simple'">
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.semipublic') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("tot_semi_publiek", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.private') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("tot_privaat", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.fastChargers') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("snellaadpunten", year)), 0) }}
            </template>
            <template #third-column>
              {{ formatNumber(getRaw(generateFieldName("vermogen_snellaadpunten", year)), 0) }} kW
            </template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>-----------------</template>
            <template #second-column>----------------</template>
            <template #third-column>------------------</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              <i> {{ $t('components.map.tiles.ElaadForecast.total') }} </i>
            </template>
            <template #second-column>
              <i>{{ formatNumber(getRaw(generateFieldName("tot_overall", year)), 0) }}</i>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>-----------------</template>
            <template #second-column>----------------</template>
            <template #third-column>------------------</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('gerealiseerde laadpunten') }}
            </template>
            <template #second-column>
              <b-spinner v-if="isChargingPointsLoading" small />
              <span v-else-if="isChargingPointsEmpty">
                —
                <button
                  class="btn btn-link p-0 ml-2"
                  type="button"
                  @click="retryChargingPointsInNeighborhood"
                >
                  Retry
                </button>
              </span>
              <span v-else>
                {{ formatNumber(chargingPointsInNeighborhood) }}
              </span>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('groupLabels.suggestion') }}
            </template>
            <template #second-column>
              <b-spinner v-if="isChargingPointsLoadingForStatus(statusSuggestion)" small />
              <span v-else>
                {{ formatNumber(getChargingPointsCount({ status: statusSuggestion })) }}
              </span>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.toPlan') }}
            </template>
            <template #second-column>
              <b-spinner
                v-if="isChargingPointsLoadingForStatus(statusRealized)
                  || isChargingPointsLoadingForStatus(statusInProgress)
                  || isChargingPointsLoadingForStatus(statusDefinitive)
                  || isChargingPointsLoadingForStatus(statusSuggestion)"
                small
              />
              <span v-else-if="isChargingPointsEmpty">
                —
                <button
                  class="btn btn-link p-0 ml-2"
                  type="button"
                  @click="retryChargingPointsInNeighborhood"
                >
                  Retry
                </button>
              </span>
              <span v-else>
                {{ formatNumber(remainingChargingPoints()) }}
              </span>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
        </template>

        <!-- DETAILED VIEW -->
        <template v-else-if="currentView === 'detailed'">
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.public') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("publiekelaadpunten", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.destination') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("bestemmingslaadpunten", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.garages') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("laadpunten_parkeergarages", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.lots') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("laadpunten_parkeerpleinen", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>------------------------------</template>
            <template #second-column>----------------</template>
            <template #third-column>------------------</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              <i> {{ $t('components.map.tiles.ElaadForecast.totalSP') }} </i>
            </template>
            <template #second-column>
              <i>{{ formatNumber(getRaw(generateFieldName("tot_semi_publiek", year)), 0) }}</i>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <br>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.privateHome') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("thuislaadpunten", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.privateOffice') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("werklaadpunten", year)), 0) }}
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>--------------------</template>
            <template #second-column>----------------</template>
            <template #third-column>------------------</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              <i> {{ $t('components.map.tiles.ElaadForecast.totalPrivate') }} </i>
            </template>
            <template #second-column>
              <i>{{ formatNumber(getRaw(generateFieldName("tot_privaat", year)), 0) }}</i>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <br>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.fastChargers') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("snellaadpunten", year)), 0) }}
            </template>
            <template #third-column>
              {{ formatNumber(getRaw(generateFieldName("vermogen_snellaadpunten", year)), 0) }} kW
            </template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>-----------------</template>
            <template #second-column>----------------</template>
            <template #third-column>------------------</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('gerealiseerde laadpunten') }}
            </template>
            <template #second-column>
              <b-spinner v-if="isChargingPointsLoading" small />
              <span v-else-if="isChargingPointsEmpty">
                —
                <button
                  class="btn btn-link p-0 ml-2"
                  type="button"
                  @click="retryChargingPointsInNeighborhood"
                >
                  Retry
                </button>
              </span>
              <span v-else>
                {{ formatNumber(chargingPointsInNeighborhood) }}
              </span>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
          <TileDataContentRowThreeColumn>
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.toPlan') }}
            </template>
            <template #second-column>
              <b-spinner
                v-if="isChargingPointsLoadingForStatus(statusRealized)
                  || isChargingPointsLoadingForStatus(statusInProgress)
                  || isChargingPointsLoadingForStatus(statusDefinitive)
                  || isChargingPointsLoadingForStatus(statusSuggestion)"
                small
              />
              <span v-else-if="isChargingPointsEmpty">
                —
                <button
                  class="btn btn-link p-0 ml-2"
                  type="button"
                  @click="retryChargingPointsInNeighborhood"
                >
                  Retry
                </button>
              </span>
              <span v-else>
                {{ formatNumber(remainingChargingPoints()) }}
              </span>
            </template>
            <template #third-column>-</template>
          </TileDataContentRowThreeColumn>
        </template>

        <!-- TO PLAN VIEW -->
        <template v-else>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column />
            <template #second-column>
              <b>{{ $t('components.map.tiles.ElaadForecast.prognose') }}</b>
            </template>
            <template #third-column>
              <b>{{ $t('components.map.tiles.ElaadForecast.current') }}</b>
            </template>
            <template #fourth-column>
              <b>{{ $t('components.map.tiles.ElaadForecast.toPlan') }}</b>
            </template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.semipublic') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("tot_semi_publiek", year)), 0) }}
            </template>
            <template #third-column>—</template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('components.map.tiles.ElaadForecast.fastChargers') }}
            </template>
            <template #second-column>
              {{ formatNumber(getRaw(generateFieldName("snellaadpunten", year)), 0) }}
            </template>
            <template #third-column>—</template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>--------------------</template>
            <template #second-column>--------------------</template>
            <template #third-column>--------------------</template>
            <template #fourth-column>--------------------</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('groupLabels.realized') }}
            </template>
            <template #second-column>—</template>
            <template #third-column>
              <b-spinner v-if="isChargingPointsLoadingForStatus(statusRealized)" small />
              <span v-else-if="isChargingPointsEmptyForStatus(statusRealized)">—</span>
              <span v-else>
                {{ formatNumber(getChargingPointsCount({ status: statusRealized }), 0) }}
              </span>
            </template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('groupLabels.in-progress') }}
            </template>
            <template #second-column>—</template>
            <template #third-column>
              <b-spinner v-if="isChargingPointsLoadingForStatus(statusInProgress)" small />
              <span v-else-if="isChargingPointsEmptyForStatus(statusInProgress)">—</span>
              <span v-else>
                {{ formatNumber(getChargingPointsCount({ status: statusInProgress }), 0) }}
              </span>
            </template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('groupLabels.definitive') }}
            </template>
            <template #second-column>—</template>
            <template #third-column>
              <b-spinner v-if="isChargingPointsLoadingForStatus(statusDefinitive)" small />
              <span v-else-if="isChargingPointsEmptyForStatus(statusDefinitive)">—</span>
              <span v-else>
                {{ formatNumber(getChargingPointsCount({ status: statusDefinitive }), 0) }}
              </span>
            </template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              {{ $t('groupLabels.suggestion') }}
            </template>
            <template #second-column>—</template>
            <template #third-column>
              <b-spinner v-if="isChargingPointsLoadingForStatus(statusSuggestion)" small />
              <span v-else-if="isChargingPointsEmptyForStatus(statusSuggestion)">—</span>
              <span v-else>
                {{ formatNumber(getChargingPointsCount({ status: statusSuggestion }), 0) }}
              </span>
            </template>
            <template #fourth-column>—</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>--------------------</template>
            <template #second-column>--------------------</template>
            <template #third-column>--------------------</template>
            <template #fourth-column>--------------------</template>
          </TileDataContentRow>
          <TileDataContentRow :nr-of-columns="4">
            <template #first-column>
              <i> {{ $t('components.map.tiles.ElaadForecast.total') }} </i>
            </template>
            <template #second-column>
              {{ formatNumber(totalExcludingPrivate(), 0) }}
            </template>
            <template #third-column>
              <b-spinner
                v-if="isChargingPointsLoadingForStatus(statusRealized)
                  || isChargingPointsLoadingForStatus(statusInProgress)
                  || isChargingPointsLoadingForStatus(statusDefinitive)
                  || isChargingPointsLoadingForStatus(statusSuggestion)"
                small
              />
              <span v-else>
                {{ formatNumber(currentChargingPoints(), 0) }}
              </span>
            </template>
            <template #fourth-column>
              <b-spinner
                v-if="isChargingPointsLoadingForStatus(statusRealized)
                  || isChargingPointsLoadingForStatus(statusInProgress)
                  || isChargingPointsLoadingForStatus(statusDefinitive)
                  || isChargingPointsLoadingForStatus(statusSuggestion)"
                small
              />
              <span v-else>
                {{ formatNumber(remainingChargingPoints(), 0) }}
              </span>
            </template>
          </TileDataContentRow>
        </template>
      </template>

      <template #footer>
        ID: {{ fids[0] }}
      </template>
    </TileDataContent>
  </div>
</template>

<script>
import TileDataContent from '@/components/map/tiles/TileDataContent'
import TiledataMixin from '@/mixins/map/tiledata-mixin'
import TileDataContentHeader from '@/components/map/tiles/TileDataContentHeader'
import TileDataContentRow from '@/components/map/tiles/TileDataContentRow'
import TileDataContentRowThreeColumn from '@/components/map/tiles/TileDataContentRowThreeColumn'
import { mapActions, mapGetters } from 'vuex'
import { getChargingpointByPolygon } from '@/../shared/API/chargingpoints'
import { buildPolygonPayloadCoordinates } from '@/helpers/geojson'

const STATUS_REALIZED = 'realized'
const STATUS_IN_PROGRESS = 'in-progress'
const STATUS_DEFINITIVE = 'definitive'
const STATUS_SUGGESTION = 'suggestion'

export default {
  name: 'TileDataElaadForecast',
  components: {
    TileDataContentRow,
    TileDataContentRowThreeColumn,
    TileDataContentHeader,
    TileDataContent,
  },
  mixins: [TiledataMixin],
  data() {
    return {
      currentView: 'simple',
      chargingPointsByFid: {},
      chargingPointsNotFoundByFid: {},
      loadingFids: {},
      statusRealized: STATUS_REALIZED,
      statusInProgress: STATUS_IN_PROGRESS,
      statusDefinitive: STATUS_DEFINITIVE,
      statusSuggestion: STATUS_SUGGESTION,
      chargingPointStatuses: [
        STATUS_REALIZED,
        STATUS_IN_PROGRESS,
        STATUS_DEFINITIVE,
        STATUS_SUGGESTION,
      ],
      tileDataContainerEl: null,
    }
  },
  computed: {
    ...mapGetters('tilesets', [
      'GetNeighborhoodPolygon',
    ]),
    ...mapGetters('access', [
      'getActiveMunicipality',
    ]),
    currentFid() {
      return this.fids && this.fids[0] ? this.fids[0] : null
    },
    chargingPointsInNeighborhood() {
      return this.getChargingPointsCount({ status: STATUS_REALIZED })
    },
    isChargingPointsLoading() {
      return this.isChargingPointsLoadingForStatus(STATUS_REALIZED)
    },
    isChargingPointsEmpty() {
      return this.isChargingPointsEmptyForStatus(STATUS_REALIZED)
    },
  },
  watch: {
    currentFid: {
      immediate: true,
      handler(fid) {
        if (fid) {
          this.loadChargingPointsForStatuses(fid)
        }
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.tileDataContainerEl = this.$el.closest('.TileData')
    })
  },
  beforeDestroy() {
    if (this.tileDataContainerEl) {
      this.tileDataContainerEl.classList.remove('TileData--wide')
      this.tileDataContainerEl = null
    }
  },
  methods: {
    ...mapActions('tilesets', [
      'fetchNeighborhoodPolygon',
    ]),
    setView(view) {
      this.currentView = view
      const el = this.$el?.closest('.TileData')
      if (el) {
        el.classList.toggle('TileData--wide', view === 'toPlan')
        this.tileDataContainerEl = el
      }
    },
    loadChargingPointsForStatuses(fid) {
      this.chargingPointStatuses.forEach((status) => {
        this.loadChargingPointsInNeighborhood(fid, status)
      })
    },
    getChargingPointsKey({ fid, status } = {}) {
      return `${fid || 'unknown'}:${status || 'all'}`
    },
    getChargingPointsCount({ fid = this.currentFid, status = STATUS_REALIZED } = {}) {
      if (!fid) return 0
      const key = this.getChargingPointsKey({ fid, status })
      const value = this.chargingPointsByFid[key]
      return value === undefined || value === null ? 0 : value
    },
    isChargingPointsLoadingForStatus(status) {
      if (!this.currentFid) return false
      const key = this.getChargingPointsKey({ fid: this.currentFid, status })
      if (this.loadingFids[key]) return true
      return !Object.prototype.hasOwnProperty.call(this.chargingPointsByFid, key)
    },
    isChargingPointsEmptyForStatus(status) {
      if (!this.currentFid) return false
      const key = this.getChargingPointsKey({ fid: this.currentFid, status })
      if (!Object.prototype.hasOwnProperty.call(this.chargingPointsByFid, key)) {
        return false
      }
      if (this.chargingPointsNotFoundByFid[key]) {
        return false
      }
      return this.chargingPointsByFid[key] === 0
    },
    generateFieldName(baseName, year) {
      const suffix = this.forecast.endsWith('low')
        ? 'L'
        : this.forecast.endsWith('mid')
        ? 'M'
        : this.forecast.endsWith('high')
        ? 'H'
        : ''
      return `${baseName}_${suffix}_${year}`
    },
    formatNumber(value, decimals = 1) {
      if (decimals === 0) return new Intl.NumberFormat().format(Math.round(value))
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)
    },
    getRaw(field) {
      const fid = this.fids && this.fids[0]
      if (!fid || !this.dataById || !this.dataById[fid]) return 0
      const val = this.dataById[fid][field]
      const num = Number(val)
      return isNaN(num) ? 0 : num
    },
    totalExcludingPrivate() {
      const year = this.year || (new Date()).getFullYear()
      const semiPublic = this.getRaw(this.generateFieldName('tot_semi_publiek', year))
      const fastChargers = this.getRaw(this.generateFieldName('snellaadpunten', year))
      return semiPublic + fastChargers
    },
    currentChargingPoints() {
      const realized = this.getChargingPointsCount({ status: STATUS_REALIZED })
      const inProgress = this.getChargingPointsCount({ status: STATUS_IN_PROGRESS })
      const definitive = this.getChargingPointsCount({ status: STATUS_DEFINITIVE })
      const suggestion = this.getChargingPointsCount({ status: STATUS_SUGGESTION })
      return realized + inProgress + definitive + suggestion
    },
    remainingChargingPoints() {
      const total = this.totalExcludingPrivate()
      const current = this.currentChargingPoints()
      const remaining = total - current
      return remaining < 0 ? 0 : remaining
    },
    async loadChargingPointsInNeighborhood(fid, status = STATUS_REALIZED) {
      const key = this.getChargingPointsKey({ fid, status })
      if (!fid || this.loadingFids[key]) return

      if (this.chargingPointsByFid[key] !== undefined) return

      this.$set(this.loadingFids, key, true)

      try {
        const cachedNeighborhood = this.GetNeighborhoodPolygon(fid)
        const neighborhood = cachedNeighborhood || await this.fetchNeighborhoodPolygon({ fid })

        if (!neighborhood) {
          this.$set(this.chargingPointsByFid, key, 0)
          return
        }

        const geometry = neighborhood?.geometry
        const payloadCoordinates = buildPolygonPayloadCoordinates({ geometry })
        const response = await getChargingpointByPolygon(payloadCoordinates, { status })
        if (response?.status === 404) {
          this.$set(this.chargingPointsByFid, key, 0)
          this.$set(this.chargingPointsNotFoundByFid, key, true)
          return
        }
        this.$set(this.chargingPointsNotFoundByFid, key, false)
        const responseData = response?.data ?? response
        const rawChargingpoints = Array.isArray(responseData) ? responseData
          : Array.isArray(responseData?.chargingpoints) ? responseData.chargingpoints
          : Array.isArray(responseData?.data) ? responseData.data
          : null
        const chargingpoints = rawChargingpoints !== null
          ? rawChargingpoints.filter(cp => cp.code === this.getActiveMunicipality)
          : null
        const count = chargingpoints !== null
          ? this.getOutletCount(chargingpoints)
          : this.getChargingPointCount(responseData)
        this.$set(this.chargingPointsByFid, key, count)
      } catch (error) {
        console.log('Could not fetch charging points for neighborhood', error) // eslint-disable-line no-console
        this.$set(this.chargingPointsByFid, key, 0)
        this.$set(this.chargingPointsNotFoundByFid, key, false)
      } finally {
        this.$set(this.loadingFids, key, false)
      }
    },
    retryChargingPointsInNeighborhood(status = STATUS_REALIZED) {
      if (!this.currentFid) return
      const key = this.getChargingPointsKey({ fid: this.currentFid, status })
      this.$delete(this.chargingPointsByFid, key)
      this.$delete(this.chargingPointsNotFoundByFid, key)
      this.loadChargingPointsInNeighborhood(this.currentFid, status)
    },
    getOutletCount(chargingpoints) {
      return chargingpoints.reduce((sum, cp) => {
        const nroutlets = cp?.properties?.nroutlets
        return sum + (typeof nroutlets === 'number' ? nroutlets : 2)
      }, 0)
    },
    getChargingPointCount(response) {
      if (response === undefined || response === null) return 0
      if (typeof response === 'number') return response
      if (Array.isArray(response)) return response.length
      if (Array.isArray(response.chargingpoints)) return response.chargingpoints.length
      if (Array.isArray(response.data)) return response.data.length
      if (typeof response.count === 'number') return response.count
      if (typeof response.total === 'number') return response.total
      return 0
    },
  },
}
</script>

<style>
.currentView {
  color: var(--text-muted);
}

.otherView {
  cursor: pointer;

  &:hover {
    color: var(--cta);
  }
}

.TileData.TileData--wide {
  width: 520px;
  transform: translateX(520px);
}

.TileData.TileData--wide.open {
  transform: translateX(0);
}

.TileDataElaadForecast td {
  white-space: nowrap;
  vertical-align: middle;
}
</style>
